import { Bell, Search } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { AlertsModal } from "../components/AlertsModal";
import logo from "../logo.svg";
import { navigate } from "../router";
import { useWsStatus } from "../ws";
import { isNavActive, NAV } from "./nav";

/**
 * Connection state is structural, not a product state, so it stays neutral
 * except for a genuine disconnect: "connecting" is a transient, expected
 * blip (page load, brief reconnect) and shouldn't read as an error, so only
 * "closed" (offline) gets the fault hue. The label always names the state,
 * so colour is never the only signal.
 */
function WsDot({ status }: { status: string }) {
	const label = status === "open" ? "Live" : status === "connecting" ? "Connecting" : "Offline";
	return (
		<span
			className={`inline-flex items-center gap-1.5 text-xs ${
				status === "closed" ? "font-medium text-fault" : "text-muted"
			}`}
		>
			<span
				className={`size-2 rounded-full ${
					status === "closed" ? "bg-fault" : status === "open" ? "bg-drop" : "bg-muted"
				}`}
			/>
			{label}
		</span>
	);
}

function AlertsBellButton({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label="Open alerts"
			title="Alerts"
			className="flex size-tap items-center justify-center rounded-md text-muted hover:bg-raised hover:text-ink"
		>
			<Bell className="size-4" aria-hidden />
		</button>
	);
}

function Wordmark() {
	return (
		<a href="#/" className="flex shrink-0 items-center gap-2 rounded-md pr-1">
			<img src={logo} alt="" className="size-6" />
			<span className="text-[15px] font-bold font-tag tracking-tight">scalpmania</span>
		</a>
	);
}

/**
 * Jump-to-item search. It hands off to Items rather than searching here, so
 * there is one search behaviour in the app; "/" focuses it from anywhere.
 */
function QuickSearch() {
	const [q, setQ] = useState("");
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
			const t = e.target as HTMLElement | null;
			if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
			e.preventDefault();
			ref.current?.focus();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	return (
		<form
			role="search"
			onSubmit={(e) => {
				e.preventDefault();
				const v = q.trim();
				navigate(v ? `/items?q=${encodeURIComponent(v)}` : "/items");
				setQ("");
				ref.current?.blur();
			}}
			className="relative hidden w-72 md:block"
		>
			<Search
				className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted"
				aria-hidden
			/>
			<input
				ref={ref}
				type="search"
				value={q}
				onChange={(e) => setQ(e.target.value)}
				placeholder="Find an item or EAN"
				aria-label="Find an item"
				className="peer h-8 w-full rounded-lg border border-hairline bg-paper pr-8 pl-8 text-[13px] text-ink outline-none placeholder:text-muted focus:border-line"
			/>
			<kbd className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 rounded border border-hairline px-1 text-[10px] leading-4 text-muted peer-focus:hidden">
				/
			</kbd>
		</form>
	);
}

export function AppShell({ current, children }: { current: string; children: ReactNode }) {
	const status = useWsStatus();
	const [alertsOpen, setAlertsOpen] = useState(false);

	return (
		<div className="min-h-screen bg-ground text-ink">
			<header className="sticky top-0 z-30 border-b border-hairline bg-ground/85 backdrop-blur-md">
				<div className="mx-auto flex h-12 max-w-[1760px] items-center gap-3 px-3 sm:gap-5 sm:px-5 lg:px-6">
					<Wordmark />

					<nav aria-label="Primary" className="hidden sm:block">
						<ul className="flex items-center gap-0.5">
							{NAV.map(({ href, label, Icon }) => {
								const active = isNavActive(current, href);
								return (
									<li key={href}>
										<a
											href={`#${href}`}
											aria-current={active ? "page" : undefined}
											className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] ${
												active
													? "bg-paper font-semibold text-ink shadow-[0_0_0_1px_var(--hairline)]"
													: "text-muted hover:text-ink"
											}`}
										>
											<Icon className="size-3.5" strokeWidth={2.25} aria-hidden />
											{label}
										</a>
									</li>
								);
							})}
						</ul>
					</nav>

					<div className="ml-auto flex items-center gap-2 sm:gap-3">
						{!isNavActive(current, "/items") && <QuickSearch />}
						<WsDot status={status} />
						<AlertsBellButton onClick={() => setAlertsOpen(true)} />
					</div>
				</div>
			</header>

			<main className="min-w-0 pb-20 sm:pb-0">{children}</main>

			{/* Phone bottom bar — three targets, a thumb's reach apart. */}
			<nav
				aria-label="Primary"
				className="fixed inset-x-0 bottom-0 z-30 border-t border-hairline bg-paper/95 backdrop-blur-md sm:hidden"
				style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
			>
				<ul className="flex items-stretch">
					{NAV.map(({ href, label, Icon }) => {
						const active = isNavActive(current, href);
						return (
							<li key={href} className="flex-1">
								<a
									href={`#${href}`}
									aria-current={active ? "page" : undefined}
									className={`flex h-14 flex-col items-center justify-center gap-1 text-[11px] ${
										active ? "font-semibold text-ink" : "text-muted"
									}`}
								>
									<Icon className="size-5" strokeWidth={active ? 2.25 : 1.75} aria-hidden />
									<span className="leading-none">{label}</span>
								</a>
							</li>
						);
					})}
				</ul>
			</nav>

			<AlertsModal open={alertsOpen} onClose={() => setAlertsOpen(false)} />
		</div>
	);
}
